import { Children, isValidElement, cloneElement, type ReactElement, type ReactNode } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/AppText';
import { useHaptics } from '@/hooks/useHaptics';
import { JourneyIcon, type JourneyIconName } from './journey-icons';
import { TEXT_START, useJourneyPalette } from './journey-theme';

export interface JourneyRowProps {
  icon?: JourneyIconName;
  /** Custom leading art (e.g. the CV mark); wins over `icon`. */
  leading?: ReactNode;
  title: string;
  subtitle?: string | null;
  value?: string | null;
  onPress?: () => void;
  tone?: 'default' | 'danger';
  accessibilityHint?: string;
  /** Set by `JourneyGroup`: draws the inset hairline above this row. */
  separated?: boolean;
}

/**
 * Grouped settings row (design `.p-row`): 19pt leading icon, title + optional
 * caption, optional trailing value, forward chevron. Plain `row` so it mirrors
 * under native RTL together with the rest of the app chrome.
 */
export function JourneyRow({
  icon,
  leading,
  title,
  subtitle,
  value,
  onPress,
  tone = 'default',
  accessibilityHint,
  separated,
}: JourneyRowProps) {
  const palette = useJourneyPalette();
  const haptics = useHaptics();
  const color = tone === 'danger' ? palette.red : palette.text;
  const iconColor = tone === 'danger' ? palette.red : palette.secondary;
  return (
    <Pressable
      onPress={
        onPress
          ? () => {
              haptics.trigger('light');
              onPress();
            }
          : undefined
      }
      disabled={!onPress}
      accessibilityRole={onPress ? 'button' : undefined}
      accessibilityLabel={[title, subtitle, value].filter(Boolean).join(', ')}
      accessibilityHint={accessibilityHint}
      style={({ pressed }) => [
        styles.row,
        pressed && onPress ? { backgroundColor: palette.line } : null,
      ]}
    >
      {separated ? (
        <View style={[styles.separator, { backgroundColor: palette.line }]} />
      ) : null}
      {leading ?? (icon ? <JourneyIcon name={icon} size={20} color={iconColor} /> : null)}
      <View style={styles.copy}>
        <Text style={[styles.title, { color }, TEXT_START]}>{title}</Text>
        {subtitle ? (
          <Text style={[styles.subtitle, { color: palette.secondary }, TEXT_START]}>
            {subtitle}
          </Text>
        ) : null}
      </View>
      {value ? (
        <Text style={[styles.value, { color: palette.secondary }]}>{value}</Text>
      ) : null}
      {onPress ? <JourneyIcon name="chevron" size={16} color={iconColor} /> : null}
    </Pressable>
  );
}

/** Rounded grouped container (`.group-list`); separates its rows. */
export function JourneyGroup({ children }: { children: ReactNode }) {
  const palette = useJourneyPalette();
  const rows = Children.toArray(children).filter(isValidElement) as ReactElement<JourneyRowProps>[];
  return (
    <View style={[styles.group, { backgroundColor: palette.group }]}>
      {rows.map((row, index) => cloneElement(row, { separated: index > 0 }))}
    </View>
  );
}

const styles = StyleSheet.create({
  group: { borderRadius: 14, overflow: 'hidden' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minHeight: 59,
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  separator: {
    position: 'absolute',
    top: 0,
    start: 47,
    end: 0,
    height: StyleSheet.hairlineWidth,
  },
  copy: { flex: 1, minWidth: 0, gap: 3 },
  title: { fontSize: 15, fontWeight: '500', lineHeight: 20 },
  subtitle: { fontSize: 13, lineHeight: 18 },
  value: { fontSize: 14 },
});
